/**
 * LA CAMPANITA — avisos y novedades.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  DOS COSAS DISTINTAS EN EL MISMO SITIO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 *   **Avisos**    — problemas de AHORA: la cuenta vence, la tasa está vieja,
 *                   hay productos agotados. Los calcula el servidor y
 *                   desaparecen solos cuando el problema se arregla.
 *
 *   **Novedades** — qué hay de nuevo en Softfi. Son fijas y se marcan como
 *                   leídas en este dispositivo.
 *
 * Van juntas porque para el usuario es el mismo gesto ("¿hay algo que deba
 * saber?"), y separarlas en dos iconos obligaría a revisar dos sitios.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  EL PUNTO ROJO SOLO PARA LO CRÍTICO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * El globo rojo con número aparece **solo si hay algo crítico** (la cuenta a
 * punto de bloquearse). Lo demás pone un punto discreto.
 *
 * Es deliberado: si todo lleva globo rojo, el rojo deja de significar nada y la
 * gente aprende a ignorarlo — justo a tiempo para ignorar también el aviso de
 * que mañana se le para el negocio.
 */

import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Bell, Check, Megaphone, Sparkles, X, MessageCircle } from "lucide-react";
import { cx } from "@/components/ui";
import { avisos as endpointAvisos } from "@/lib/endpoints";
import { NOVEDADES, ULTIMA_NOVEDAD } from "./novedades";

const CLAVE_LEIDAS = "softfi.novedades-leidas";

function ultimaLeida(): string {
  try {
    return localStorage.getItem(CLAVE_LEIDAS) ?? "";
  } catch {
    return "";
  }
}

export function Campanita({ compacto = false }: { compacto?: boolean }) {
  const [abierto, setAbierto] = useState(false);
  const [pestana, setPestana] = useState<"avisos" | "novedades">("avisos");
  const [leida, setLeida] = useState(ultimaLeida);
  const contenedor = useRef<HTMLDivElement>(null);

  const { data } = useQuery({
    queryKey: ["avisos"],
    queryFn: endpointAvisos.listar,
    // Cada 5 minutos. Los avisos cambian despacio (una tasa vieja sigue vieja
    // dentro de un minuto), así que consultar más seguido sería gastar batería
    // del celular del cajero para nada.
    refetchInterval: 5 * 60_000,
    staleTime: 60_000,
  });

  const listaAvisos = data?.avisos ?? [];
  const criticos = data?.criticos ?? 0;
  const hayNovedades = ULTIMA_NOVEDAD !== "" && leida !== ULTIMA_NOVEDAD;
  const hayAlgo = listaAvisos.length > 0 || hayNovedades;

  // Cerrar al hacer clic fuera o con Escape.
  useEffect(() => {
    if (!abierto) return;

    const alClicFuera = (e: MouseEvent) => {
      if (contenedor.current && !contenedor.current.contains(e.target as Node)) {
        setAbierto(false);
      }
    };
    const alEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") setAbierto(false);
    };

    document.addEventListener("mousedown", alClicFuera);
    document.addEventListener("keydown", alEscape);
    return () => {
      document.removeEventListener("mousedown", alClicFuera);
      document.removeEventListener("keydown", alEscape);
    };
  }, [abierto]);

  function marcarNovedadesLeidas() {
    try {
      localStorage.setItem(CLAVE_LEIDAS, ULTIMA_NOVEDAD);
    } catch {
      /* Modo incógnito: se vuelven a mostrar. No pasa nada. */
    }
    setLeida(ULTIMA_NOVEDAD);
  }

  return (
    <div className="relative" ref={contenedor}>
      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        aria-label={
          criticos > 0 ? `Avisos (${criticos} urgentes)` : hayAlgo ? "Avisos nuevos" : "Avisos"
        }
        className={cx(
          "relative rounded-[var(--radius-control)] p-2.5 transition-colors",
          abierto ? "bg-surface-hover text-ink" : "text-muted hover:bg-surface-hover hover:text-ink",
        )}
      >
        <Bell size={18} />

        {criticos > 0 ? (
          <span className="absolute -right-0.5 -top-0.5 flex size-4 items-center justify-center rounded-full bg-danger text-[10px] font-bold text-white">
            {criticos}
          </span>
        ) : hayAlgo ? (
          <span className="absolute right-1 top-1 size-2 rounded-full bg-accent" />
        ) : null}
      </button>

      {abierto && (
        <div
          className={cx(
            "animar-entrada absolute z-50 mt-2 w-[min(22rem,calc(100vw-2rem))]",
            "rounded-[var(--radius-modal)] border border-line bg-surface shadow-[var(--shadow-pop)]",
            // En la barra lateral se abre hacia la derecha; en la barra
            // superior del celular, hacia la izquierda para no salirse.
            compacto ? "right-0" : "left-0",
          )}
        >
          {/* Pestañas */}
          <div className="flex items-center gap-1 border-b border-line p-2">
            <BotonPestana
              activo={pestana === "avisos"}
              onClick={() => setPestana("avisos")}
              icono={<Bell size={14} />}
              texto="Avisos"
              cuenta={listaAvisos.length}
            />
            <BotonPestana
              activo={pestana === "novedades"}
              onClick={() => {
                setPestana("novedades");
                marcarNovedadesLeidas();
              }}
              icono={<Sparkles size={14} />}
              texto="Novedades"
              punto={hayNovedades}
            />
            <button
              type="button"
              onClick={() => setAbierto(false)}
              aria-label="Cerrar"
              className="ml-auto rounded-[var(--radius-control)] p-1.5 text-muted hover:bg-surface-hover hover:text-ink"
            >
              <X size={15} />
            </button>
          </div>

          <div className="scroll-fino max-h-[min(28rem,70vh)] overflow-y-auto">
            {pestana === "avisos" ? (
              listaAvisos.length === 0 ? (
                <div className="px-5 py-10 text-center">
                  <Check size={26} className="mx-auto mb-3 text-success" />
                  <p className="text-sm font-medium text-ink">Todo en orden</p>
                  <p className="mt-1 text-xs text-muted">
                    No hay nada que necesite tu atención ahora mismo.
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-line">
                  {listaAvisos.map((a) => (
                    <FilaAviso key={a.id} aviso={a} onIr={() => setAbierto(false)} />
                  ))}
                </div>
              )
            ) : (
              <div className="divide-y divide-line">
                {NOVEDADES.map((n) => (
                  <div key={n.id} className="p-4">
                    <div className="mb-2 flex items-center gap-2">
                      <Megaphone size={14} className="shrink-0 text-accent" />
                      <p className="min-w-0 flex-1 text-sm font-semibold text-ink">{n.titulo}</p>
                    </div>
                    <p className="mb-2.5 text-[11px] text-faint">
                      {new Date(n.fecha + "T12:00:00").toLocaleDateString("es-VE", {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                      })}
                    </p>
                    <ul className="space-y-2">
                      {n.puntos.map((p, i) => (
                        <li key={i} className="flex gap-2 text-[13px] leading-relaxed text-ink-soft">
                          <span className="mt-1.5 size-1 shrink-0 rounded-full bg-accent" />
                          <span dangerouslySetInnerHTML={{ __html: negritas(p) }} />
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------

function BotonPestana({
  activo,
  onClick,
  icono,
  texto,
  cuenta,
  punto,
}: {
  activo: boolean;
  onClick: () => void;
  icono: React.ReactNode;
  texto: string;
  cuenta?: number;
  punto?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cx(
        "flex items-center gap-1.5 rounded-[var(--radius-control)] px-2.5 py-1.5 text-xs font-medium transition-colors",
        activo ? "bg-accent-soft text-accent" : "text-muted hover:text-ink",
      )}
    >
      {icono}
      {texto}
      {cuenta !== undefined && cuenta > 0 && (
        <span className="rounded-full bg-surface-2 px-1.5 text-[10px]">{cuenta}</span>
      )}
      {punto && <span className="size-1.5 rounded-full bg-accent" />}
    </button>
  );
}

function FilaAviso({
  aviso,
  onIr,
}: {
  aviso: import("@/lib/types").AvisoApi;
  onIr: () => void;
}) {
  const colores = {
    critico: "bg-danger",
    aviso: "bg-warning",
    info: "bg-accent",
  } as const;

  return (
    <div className="flex gap-3 p-4">
      {/* La barrita de color a la izquierda: dice la urgencia sin leer nada. */}
      <span className={cx("mt-1 w-1 shrink-0 rounded-full", colores[aviso.nivel])} />

      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium leading-snug text-ink">{aviso.titulo}</p>
        <p className="mt-1 text-[13px] leading-relaxed text-muted">{aviso.texto}</p>

        {(aviso.accion_url || aviso.whatsapp) && (
          <div className="mt-2.5 flex flex-wrap items-center gap-2">
            {aviso.accion_url && aviso.accion_texto && (
              <Link
                to={aviso.accion_url}
                onClick={onIr}
                className="pulsable rounded-[var(--radius-control)] bg-accent px-2.5 py-1.5 text-xs font-semibold text-on-accent"
              >
                {aviso.accion_texto}
              </Link>
            )}

            {/* El botón de WhatsApp va con el color de WhatsApp a propósito: se
                reconoce sin leer, y este público lo busca antes que cualquier
                otra cosa cuando tiene una duda de dinero. */}
            {aviso.whatsapp && (
              <a
                href={aviso.whatsapp}
                target="_blank"
                rel="noreferrer"
                onClick={onIr}
                className="pulsable inline-flex items-center gap-1.5 rounded-[var(--radius-control)] border border-[#25D366]/40 bg-[#25D366]/10 px-2.5 py-1.5 text-xs font-semibold text-[#1DA851] hover:bg-[#25D366]/20 dark:text-[#4ADE80]"
              >
                <MessageCircle size={12} />
                {aviso.whatsapp_texto ?? "Escribir por WhatsApp"}
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Convierte los `**dobles asteriscos**` en negrita.
 *
 * Es un mini-formateador de tres líneas en vez de traer una librería de
 * markdown de 40 KB para poner una palabra en negrita. Se escapa el HTML
 * primero: el texto sale de nuestro propio archivo de novedades, pero escapar
 * cuesta una línea y evita que un día alguien pegue ahí algo con `<script>`.
 */
function negritas(texto: string): string {
  const seguro = texto
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  return seguro.replace(/\*\*(.+?)\*\*/g, "<strong class='text-ink'>$1</strong>");
}
